import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Grid,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface SecuritySettingsProps {
  passwordData: any;
  error: string;
  success: string;
  onPasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPasswordSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isChanging: boolean;
}

const SecuritySettings: React.FC<SecuritySettingsProps> = ({
  passwordData,
  error,
  success,
  onPasswordChange,
  onPasswordSubmit,
  onCancel,
  isChanging,
}) => {
  const { t } = useTranslation('profile');
  const passwordsMatch = passwordData.new_password === passwordData.confirm_password;
  const isFormValid = passwordData.current_password && 
                     passwordData.new_password && 
                     passwordData.confirm_password && 
                     passwordsMatch;

  return (
    <Box component="form" onSubmit={onPasswordSubmit}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        {t('security.changePassword')}
      </Typography>

      <Grid container spacing={3}>
        <Grid size={12}>
          <TextField
            fullWidth
            label={t('security.currentPassword')}
            name="current_password"
            type="password"
            value={passwordData.current_password}
            onChange={onPasswordChange}
            placeholder={t('security.currentPasswordPlaceholder')}
            sx={{ 
              borderRadius: 2,
              mb: 2,
              '& .MuiInputBase-input': {
                transition: 'none'
              }
            }}
          />
        </Grid>
        
        <Grid size={12}>
          <TextField
            fullWidth
            label={t('security.newPassword')}
            name="new_password"
            type="password"
            value={passwordData.new_password}
            onChange={onPasswordChange}
            placeholder={t('security.newPasswordPlaceholder')}
            sx={{ 
              borderRadius: 2,
              mb: 2,
              '& .MuiInputBase-input': {
                transition: 'none'
              }
            }}
          />
        </Grid>
        
        <Grid size={12}>
          <TextField
            fullWidth
            label={t('security.confirmPassword')}
            name="confirm_password"
            type="password"
            value={passwordData.confirm_password}
            onChange={onPasswordChange}
            placeholder={t('security.confirmPasswordPlaceholder')}
            error={passwordData.confirm_password && !passwordsMatch}
            helperText={passwordData.confirm_password && !passwordsMatch ? t('security.errors.mismatch') : ''}
            sx={{ 
              borderRadius: 2,
              mb: 2,
              '& .MuiInputBase-input': {
                transition: 'none'
              }
            }}
          />
        </Grid>
      </Grid>

      {/* Password Requirements */}
      <Box sx={{ mt: 4, mb: 3 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          {t('security.requirements.title')}
        </Typography>
        <Box component="ul" sx={{ pl: 2, m: 0 }}>
          <li style={{ marginBottom: 12 }}>
            <Typography variant="body2" color="text.secondary">
              {t('security.requirements.minLength')}
            </Typography>
          </li>
          <li style={{ marginBottom: 12 }}>
            <Typography variant="body2" color="text.secondary">
              {t('security.requirements.uppercase')}
            </Typography>
          </li>
          <li style={{ marginBottom: 12 }}>
            <Typography variant="body2" color="text.secondary">
              {t('security.requirements.lowercase')}
            </Typography>
          </li>
          <li style={{ marginBottom: 12 }}>
            <Typography variant="body2" color="text.secondary">
              {t('security.requirements.number')}
            </Typography>
          </li>
        </Box>
      </Box>
      
      {/* Alerts */}
      {(error || success) && (
        <Box sx={{ mt: 3 }}>
          {error && (
            <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ borderRadius: 2, mb: 2 }}>
              {success}
            </Alert>
          )}
        </Box>
      )}
      
      {/* Action Buttons */}
      <Box sx={{ 
        mt: 3, 
        display: 'flex', 
        gap: 2, 
        justifyContent: 'flex-end',
        alignItems: 'center'
      }}>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={onCancel}
          disabled={isChanging}
          sx={{ borderRadius: 1.5 }}
        >
          {t('security.cancel')}
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={isChanging ? undefined : <SaveIcon />}
          disabled={isChanging || !isFormValid}
          sx={{ 
            borderRadius: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #7b8cff 0%, #ff5b8d 100%)',
            }
          }}
        >
          {isChanging ? t('security.changingPassword') : t('security.changePassword')}
        </Button>
      </Box>
    </Box>
  );
};

export default SecuritySettings;
